import React, { useState, useEffect } from 'react';
import { CATEGORIES } from '../utils/categories';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase/config';

const AdminProductForm = ({ product, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    longDescription: '',
    price: '',
    image: '',
    ingredients: '',
    category: '',
    variants: [],
    available: true
  });

  const categories = CATEGORIES;

  const [newVariant, setNewVariant] = useState({
    name: '',
    price: '',
    originalPrice: '',
    description: ''
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        description: product.description || '',
        longDescription: product.longDescription || '',
        price: product.price || '',
        image: product.image || '',
        ingredients: product.ingredients || '',
        category: product.category || '',
        variants: product.variants || [],
        available: product.available !== false
      });
      setImagePreview(product.image || '');
    } else {
      // Reset form when no product (adding new)
      setFormData({
        name: '',
        description: '',
        longDescription: '',
        price: '',
        image: '',
        ingredients: '',
        category: '',
        variants: [],
        available: true
      });
      setImagePreview('');
      setImageFile(null);
    }
  }, [product]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }

      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return formData.image;

    setUploading(true);
    setUploadProgress(0);

    try {
      // Create a unique filename
      const timestamp = Date.now();
      const filename = `products/${timestamp}_${imageFile.name}`;
      const storageRef = ref(storage, filename);

      // Upload file
      const uploadTask = uploadBytesResumable(storageRef, imageFile);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(progress);
          },
          (error) => {
            console.error('Upload error:', error);
            setUploading(false);
            reject(error);
          },
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            setUploading(false);
            resolve(downloadURL);
          }
        );
      });
    } catch (error) {
      setUploading(false);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let imageUrl = formData.image;

      // Upload new image if selected
      if (imageFile) {
        imageUrl = await uploadImage();
      }

      // Validate image URL
      if (!imageUrl) {
        alert('Please provide a product image');
        return;
      }

      onSave({
        ...formData,
        image: imageUrl,
        id: product?.id || Date.now(),
        price: parseFloat(formData.price)
      });
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Error uploading image. Please try again.');
    }
  };

  const addVariant = () => {
    if (newVariant.name && newVariant.price) {
      setFormData({
        ...formData,
        variants: [...formData.variants, {
          ...newVariant,
          price: parseFloat(newVariant.price),
          originalPrice: newVariant.originalPrice ? parseFloat(newVariant.originalPrice) : null
        }]
      });
      setNewVariant({ name: '', price: '', originalPrice: '', description: '' });
    }
  };

  const removeVariant = (index) => {
    setFormData({
      ...formData,
      variants: formData.variants.filter((_, i) => i !== index)
    });
  };

  return (
    <form onSubmit={handleSubmit} className="admin-form">
      <button type="button" onClick={onCancel} className="form-close-btn" title="Close">
        ✕
      </button>
      <h3>{product ? 'Edit Product' : 'Add New Product'}</h3>
      
      <input
        type="text"
        placeholder="Product Name"
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        required
      />
      
      <textarea
        placeholder="Short Description"
        value={formData.description}
        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        required
      />
      
      <textarea
        placeholder="Long Description (optional)"
        value={formData.longDescription}
        onChange={(e) => setFormData({ ...formData, longDescription: e.target.value })}
      />
      
      <select
        value={formData.category}
        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
        required
        className="category-select"
      >
        <option value="">Select Category</option>
        {categories.map(cat => (
          <option key={cat} value={cat}>{cat}</option>
        ))}
      </select>
      
      <input
        type="number"
        placeholder="Base Price (per kg for Sweets)"
        value={formData.price}
        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
        required
        step="0.01"
      />
      
      <div className="image-upload-section">
        <label className="image-upload-label">Product Image</label>
        
        {imagePreview && (
          <div className="image-preview">
            <img src={imagePreview} alt="Preview" />
          </div>
        )}
        
        <div className="image-upload-options">
          <div className="upload-file-option">
            <label htmlFor="image-file-input" className="file-upload-btn">
              📁 Choose Image File
            </label>
            <input
              id="image-file-input"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{ display: 'none' }}
            />
            {imageFile && <span className="file-name">{imageFile.name}</span>}
          </div>
          
          <div className="upload-divider">OR</div>
          
          <input
            type="text"
            placeholder="Enter Image URL"
            value={formData.image}
            onChange={(e) => {
              setFormData({ ...formData, image: e.target.value });
              setImagePreview(e.target.value);
              setImageFile(null);
            }}
            className="image-url-input"
          />
        </div>

        {uploading && (
          <div className="upload-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <span className="progress-text">{Math.round(uploadProgress)}%</span>
          </div>
        )}
      </div>
      
      <input
        type="text"
        placeholder="Ingredients (optional)"
        value={formData.ingredients}
        onChange={(e) => setFormData({ ...formData, ingredients: e.target.value })}
      />

      <div className="availability-section">
        <label className="availability-label">
          <input
            type="checkbox"
            checked={formData.available !== false}
            onChange={(e) => setFormData({ ...formData, available: e.target.checked })}
          />
          <span>Product Available for Customers</span>
        </label>
      </div>

      <div className="variants-section">
        <h4>Product Variants</h4>
        
        {formData.variants.map((variant, index) => (
          <div key={index} className="variant-item">
            <span>{variant.name} - ₹{variant.price}</span>
            <button type="button" onClick={() => removeVariant(index)} className="btn-remove-variant">
              Remove
            </button>
          </div>
        ))}
        
        <div className="add-variant">
          <input
            type="text"
            placeholder="Variant Name"
            value={newVariant.name}
            onChange={(e) => setNewVariant({ ...newVariant, name: e.target.value })}
          />
          <input
            type="number"
            placeholder="Price"
            value={newVariant.price}
            onChange={(e) => setNewVariant({ ...newVariant, price: e.target.value })}
            step="0.01"
          />
          <input
            type="number"
            placeholder="Original Price (optional)"
            value={newVariant.originalPrice}
            onChange={(e) => setNewVariant({ ...newVariant, originalPrice: e.target.value })}
            step="0.01"
          />
          <input
            type="text"
            placeholder="Variant Description"
            value={newVariant.description}
            onChange={(e) => setNewVariant({ ...newVariant, description: e.target.value })}
          />
          <button type="button" onClick={addVariant} className="btn-add-variant">
            Add Variant
          </button>
        </div>
      </div>

      <div className="form-actions">
        <button type="submit" className="btn-save" disabled={uploading}>
          {uploading ? 'Uploading...' : 'Save'}
        </button>
        <button type="button" onClick={onCancel} className="btn-cancel" disabled={uploading}>
          Cancel
        </button>
      </div>
    </form>
  );
};

export default AdminProductForm;
